import type { Interpretation } from '../../core/types';
import { formatTime, trackColor } from '../util';

interface Props {
  song: Interpretation | null;
  playing: boolean;
  loop: boolean;
  position: number;
  mutes: boolean[];
  solos: boolean[];
  onPlay: () => void;
  onStop: () => void;
  onLoop: (v: boolean) => void;
  onMute: (i: number) => void;
  onSolo: (i: number) => void;
  onDownload: () => void;
  onDownloadZip: () => void;
}

/** Bottom bar: transport, per-track solo/mute and export. */
export function Player(p: Props) {
  const song = p.song;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="btn btn-primary w-24"
          onClick={p.playing ? p.onStop : p.onPlay}
          disabled={!song}
          aria-keyshortcuts="Space"
        >
          {p.playing ? '■ Stop' : '▶ Play'}
        </button>
        <label className="flex items-center gap-1 text-[12px]">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={p.loop}
            onChange={(e) => p.onLoop(e.target.checked)}
          />
          Loop
        </label>
        <span
          className="min-w-[7.5rem] font-mono text-[12px] tabular-nums"
          aria-live="off"
          aria-label="Position"
        >
          {formatTime(p.position)} / {formatTime(song?.duration ?? 0)}
        </span>
      </div>

      {song && (
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Track solo and mute">
          {song.tracks.map((t, i) => (
            <div
              key={i}
              className="flex items-center gap-0.5 rounded-md border border-line bg-panel2 py-0.5 pl-1.5 pr-0.5"
            >
              <span
                aria-hidden="true"
                className="h-2.5 w-2.5 rounded-full"
                style={{ background: trackColor(t) }}
              />
              <span className="px-1 text-xs">
                {t.kind === 'drums' ? 'Drums' : t.kind === 'merged' ? 'Voices' : `V${(t.voice ?? 0) + 1}`}
              </span>
              <button
                type="button"
                aria-pressed={p.mutes[i] ?? false}
                aria-label={`Mute ${t.name}`}
                onClick={() => p.onMute(i)}
                className={`rounded px-1.5 text-xs font-bold ${p.mutes[i] ? 'bg-danger text-bg' : 'text-muted hover:text-accent'}`}
              >
                M
              </button>
              <button
                type="button"
                aria-pressed={p.solos[i] ?? false}
                aria-label={`Solo ${t.name}`}
                onClick={() => p.onSolo(i)}
                className={`rounded px-1.5 text-xs font-bold ${p.solos[i] ? 'bg-warn text-bg' : 'text-muted hover:text-accent'}`}
              >
                S
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="ml-auto flex items-center gap-2">
        {song && (
          <span className="hidden font-mono text-[11px] text-muted sm:inline">
            {song.bpm.toFixed(2)} BPM · bend ±{song.bendRange}
          </span>
        )}
        <button type="button" className="btn btn-primary" onClick={p.onDownload} disabled={!song}>
          Download MIDI
        </button>
        <button type="button" className="btn" onClick={p.onDownloadZip} disabled={!song}>
          Per-voice ZIP
        </button>
      </div>
    </div>
  );
}
